import React, { useEffect, useState, useRef } from "react";
import { Loader2, Flame, CheckCircle2 } from "lucide-react";
import { api } from "../../lib/api";

/**
 * EnrichRegionProgress — live per-region tracker for the overnight AI
 * enrichment worker. Refreshes every 10s while the page is open.
 *
 * Two tiers:
 *   • PRIORITY: the 16 high-traffic regions the worker burns through first
 *     (Lake District → Cornwall → Snowdonia → Highlands → …). These get
 *     bookable sites first so launch-morning conversion-critical cards
 *     look best.
 *   • LONG TAIL: everything else, ordered least-done first so it's easy
 *     to spot which counties still need a follow-up overnight run.
 *
 * Auto-polls every 10s so the admin can watch progress live during the
 * 7pm-7am maintenance window. Pauses polling when the tab is hidden.
 */
export default function EnrichRegionProgressCard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const timerRef = useRef(null);

  const load = async () => {
    try {
      const { data: res } = await api.get("/admin/campsites/enrich-stats-by-region");
      setData(res);
      setErr("");
    } catch (e) {
      setErr(e?.response?.data?.detail || "Failed to load region progress");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    const tick = () => {
      if (!document.hidden) load();
    };
    timerRef.current = setInterval(tick, 10_000);
    document.addEventListener("visibilitychange", tick);
    return () => {
      clearInterval(timerRef.current);
      document.removeEventListener("visibilitychange", tick);
    };
  }, []);

  if (loading && !data) {
    return (
      <section
        className="bg-white border hairline p-5 sm:p-6 mb-6"
        data-testid="enrich-region-progress"
      >
        <div className="mono text-xs text-[#888] flex items-center gap-2">
          <Loader2 className="w-3.5 h-3.5 animate-spin" />
          Loading region progress…
        </div>
      </section>
    );
  }

  const priority = data?.priority || [];
  const rest = data?.rest || [];
  const overallTotal = [...priority, ...rest].reduce((a, r) => a + r.total, 0);
  const overallDone = [...priority, ...rest].reduce((a, r) => a + r.enriched, 0);
  const overallPct = overallTotal ? Math.round((100 * overallDone) / overallTotal) : 0;

  return (
    <section
      className="bg-white border hairline p-5 sm:p-6 mb-6"
      data-testid="enrich-region-progress"
    >
      <header className="mb-4">
        <div className="eyebrow text-[#ff4500] flex items-center gap-1.5">
          <Flame className="w-3 h-3" /> Overnight worker · region by region
        </div>
        <h2 className="font-display text-xl sm:text-2xl mt-1">
          Live enrichment progress
        </h2>
        <p className="text-xs text-[#666] mt-1.5 mono">
          Overall <strong className="text-[#111]">{overallDone.toLocaleString("en-GB")}</strong> /
          {" "}{overallTotal.toLocaleString("en-GB")} ({overallPct}%) ·
          {" "}refreshes every 10s
        </p>

        {/* Live throughput badges — confirms the worker is actually moving */}
        <div className="mt-3 flex flex-wrap gap-2" data-testid="enrich-throughput">
          <ThroughputPill
            label="last 5 min"
            value={data?.enriched_last_5min ?? 0}
            testid="enrich-throughput-5min"
            active={(data?.enriched_last_5min ?? 0) > 0}
          />
          <ThroughputPill
            label="last hour"
            value={data?.enriched_last_hour ?? 0}
            testid="enrich-throughput-hour"
            active={(data?.enriched_last_hour ?? 0) > 0}
          />
          <ThroughputPill
            label="sites / min"
            value={data?.rate_per_min ?? 0}
            testid="enrich-throughput-rate"
            active={(data?.rate_per_min ?? 0) > 0}
          />
        </div>
        {err && <p className="text-xs text-red-700 mt-2 mono">{err}</p>}
      </header>

      <div className="mb-3 eyebrow text-[#111] flex items-center gap-1.5">
        <Flame className="w-3 h-3 text-[#ff4500]" /> Priority regions (worker hits these first)
      </div>
      <RegionList rows={priority} priority data-testid-prefix="enrich-priority" />

      <div className="mt-6 mb-3 eyebrow text-[#666]">Long tail</div>
      <RegionList rows={rest} data-testid-prefix="enrich-rest" />
    </section>
  );
}

function ThroughputPill({ label, value, testid, active }) {
  return (
    <span
      data-testid={testid}
      className={`inline-flex items-baseline gap-1.5 px-2.5 py-1 text-[11px] mono uppercase tracking-[0.16em] border ${
        active ? "border-[#ff4500] bg-[#fff5f0] text-[#111]" : "hairline text-[#888]"
      }`}
    >
      <strong className="font-bold tabular-nums">{Number(value).toLocaleString("en-GB")}</strong>
      <span className="text-[#666]">{label}</span>
    </span>
  );
}

function RegionList({ rows, priority = false, ...rest }) {
  const prefix = rest["data-testid-prefix"] || "enrich-region";
  if (!rows.length) {
    return <div className="text-xs text-[#888] mono">No regions yet.</div>;
  }
  return (
    <ul className="space-y-1.5">
      {rows.map((r) => {
        const pct = Math.min(100, r.pct || 0);
        const done = pct >= 99.5;
        return (
          <li
            key={r.region}
            className="flex items-center gap-3 text-xs"
            data-testid={`${prefix}-${r.region.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
          >
            <span className={`shrink-0 truncate ${priority ? "font-bold text-[#111]" : "text-[#333]"}`} style={{ width: 180 }}>
              {r.region}
            </span>
            <div className="flex-1 bg-[#f5f5f4] h-2 relative overflow-hidden">
              <div
                className={`absolute inset-y-0 left-0 ${done ? "bg-[#0a7f3f]" : priority ? "bg-[#ff4500]" : "bg-[#a8a29e]"}`}
                style={{ width: `${pct}%`, transition: "width .4s ease" }}
              />
            </div>
            <span className="shrink-0 mono tabular-nums text-[#555]" style={{ minWidth: 90, textAlign: "right" }}>
              {r.enriched.toLocaleString("en-GB")} / {r.total.toLocaleString("en-GB")}
            </span>
            <span className="shrink-0 mono tabular-nums text-[#888]" style={{ minWidth: 42, textAlign: "right" }}>
              {pct.toFixed(0)}%
            </span>
            {done && <CheckCircle2 className="w-3.5 h-3.5 text-[#0a7f3f] shrink-0" />}
          </li>
        );
      })}
    </ul>
  );
}
